import { Controller } from '@hotwired/stimulus';

/**
 * reschedule-dates — keeps the « Date de fin proposée » input from preceding the
 * « Date de début proposée » on the reschedule (« Demander un décalage ») form.
 *
 * `start` and `end` are now the HIDDEN ISO inputs (`data-date-picker-target="hidden"`)
 * of the calendar `Form::DateInputComponent` (ISO YYYY-MM-DD). BOTH fields fire
 * `change->reschedule-dates#syncMin` (the sibling `date-picker` controller dispatches
 * `change` on every pick / valid-blur commit), so an end picked before the start snaps
 * forward too. The native `min` floor is gone (a hidden input ignores it); the snap +
 * the server validation (RescheduleRequest#proposed_dates_in_order) enforce end ≥ start.
 * `syncMin` also runs once on connect so a prefilled start constrains the end immediately.
 *
 * Writes go through `#setIso`, which dispatches a self-suppressed bubbling `change` so
 * the end `date-picker` re-syncs its FR display + popup without re-entering `syncMin`
 * (the `_suppress` guard mirrors the picker's `_internalWrite` idiom).
 *
 * Targets:
 *   • start — HIDDEN ISO <input type="hidden"> (`data-date-picker-target="hidden"`, yyyy-mm-dd)
 *   • end   — HIDDEN ISO <input type="hidden"> (`data-date-picker-target="hidden"`, yyyy-mm-dd)
 *
 * @example
 *   <form data-controller="reschedule-dates">
 *     <input type="hidden" data-reschedule-dates-target="start"
 *            data-date-picker-target="hidden"
 *            data-action="change->reschedule-dates#syncMin">
 *     <input type="hidden" data-reschedule-dates-target="end"
 *            data-date-picker-target="hidden"
 *            data-action="change->reschedule-dates#syncMin">
 *   </form>
 */
export default class extends Controller {
  static targets = ['start', 'end'];

  connect() {
    this.syncMin();
  }

  // Snap the proposed end ≥ proposed start. Fired on `change` of EITHER hidden ISO
  // field (the `date-picker` dispatches it) and on connect. The native `min` floor is
  // gone (a hidden input ignores it); the snap + the server validation
  // (RescheduleRequest#proposed_dates_in_order) enforce end ≥ start.
  syncMin() {
    if (this._suppress) return;
    if (!this.hasStartTarget || !this.hasEndTarget) return;

    const start = this.startTarget.value;
    // String compare is chronological for the zero-padded YYYY-MM-DD ISO the hidden
    // field carries.
    if (start && this.endTarget.value && this.endTarget.value < start) {
      this.#setIso(this.endTarget, start);
    }
  }

  // Same idiom as planning-phase-form: write the ISO + dispatch a self-suppressed
  // bubbling `change` so the end `date-picker` re-syncs its FR display/popup, without
  // re-entering syncMin.
  #setIso(target, iso) {
    if (target.value === iso) return;
    target.value = iso;
    this._suppress = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
    this._suppress = false;
  }
}
